import { useEffect, useMemo, useState } from "react";
import api, { fmtIDR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { buildNota, openWhatsApp } from "@/lib/waNota";
import { MessageCircle, ShoppingCart, TrendingUp, FileDown, Search } from "lucide-react";

export default function SalesHistory() {
  const { isOwner } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nota, setNota] = useState(null);
  const [form, setForm] = useState({ customerName: "", phone: "", unit: "", imei: "" });
  const [f, setF] = useState({ imei: "", cust: "", method: "all", start: "", end: "" });
  const [detail, setDetail] = useState(null);

  const load = () => { setLoading(true); api.get("/sales").then(({ data }) => setAllRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const cancelSale = async (r) => {
    if (!window.confirm(`Batalkan transaksi ${r.invoice}? Stok akan dikembalikan.`)) return;
    try { await api.put(`/sales/${r.id}/cancel`); toast.success("Transaksi dibatalkan & stok dikembalikan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
  };

  const rows = useMemo(() => allRows.filter((r) => {
    if (f.method !== "all" && r.payment_method !== f.method) return false;
    if (f.start && (r.date || "") < f.start) return false;
    if (f.end && (r.date || "") > f.end + "T23:59:59") return false;
    if (f.cust) { const c = (r.customer_name || "").toLowerCase(); if (!c.includes(f.cust.toLowerCase())) return false; }
    if (f.imei) {
      const hit = (r.items || []).some((i) => `${i.imei || ""}${i.product_name || ""}`.toLowerCase().includes(f.imei.toLowerCase()))
        || (r.trade_in && `${r.trade_in.imei || ""}`.includes(f.imei));
      if (!hit) return false;
    }
    return true;
  }), [allRows, f]);

  const active = rows.filter((r) => r.status !== "dibatalkan");
  const omset = active.reduce((s, r) => s + (r.total || 0), 0);
  const profit = active.reduce((s, r) => s + (r.profit || 0), 0);

  const openNota = (sale) => {
    const first = (sale.items || [])[0] || {};
    setForm({ customerName: sale.customer_name === "Umum" ? "" : (sale.customer_name || ""), phone: "", unit: first.product_name || "", imei: first.imei || "" });
    setNota(sale);
  };
  const sendNota = () => { openWhatsApp(form.phone, buildNota(form)); setNota(null); };

  const exportExcel = () => {
    const header = ["Invoice", "Tanggal", "Pelanggan", "Barang", "IMEI", "Total", "Laba", "Metode", "Status"];
    const lines = rows.map((r) => [
      r.invoice, fmtDate(r.date), r.customer_name,
      (r.items || []).map((i) => `${i.product_name} x${i.qty}`).join(" | "),
      (r.items || []).map((i) => i.imei).filter(Boolean).join(" "),
      r.total, r.profit ?? "", r.payment_method, r.status,
    ]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [header, ...lines].map((row) => row.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `riwayat-penjualan-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("Laporan diekspor (Excel/CSV)");
  };

  return (
    <div>
      <PageHeader title="Riwayat Penjualan" subtitle="Cari, filter & ekspor transaksi penjualan">
        <Button onClick={exportExcel} variant="outline" className="border-green-200 text-green-700" data-testid="export-excel-btn"><FileDown className="w-4 h-4 mr-1.5" /> Export Excel</Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 max-w-2xl">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs uppercase text-slate-500 font-medium"><ShoppingCart className="w-4 h-4 text-sky-500" /> Total Transaksi</div>
          <div className="text-2xl font-semibold font-mono-num mt-1">{active.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="text-xs uppercase text-slate-500 font-medium">Total Omset</div>
          <div className="text-2xl font-semibold font-mono-num text-sky-700 mt-1">{fmtIDR(omset)}</div>
        </div>
        {isOwner && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="sales-profit-card">
            <div className="flex items-center gap-2 text-xs uppercase text-slate-500 font-medium"><TrendingUp className="w-4 h-4 text-green-500" /> Total Keuntungan</div>
            <div className="text-2xl font-semibold font-mono-num text-green-600 mt-1">{fmtIDR(profit)}</div>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Cari IMEI / Barang</Label>
          <div className="relative mt-1"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={f.imei} onChange={(e) => setF({ ...f, imei: e.target.value })} className="pl-8" placeholder="IMEI / nama" data-testid="filter-imei" /></div>
        </div>
        <div><Label className="text-xs text-slate-500">Pelanggan / WA</Label><Input value={f.cust} onChange={(e) => setF({ ...f, cust: e.target.value })} className="mt-1" data-testid="filter-cust" /></div>
        <div>
          <Label className="text-xs text-slate-500">Metode Bayar</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger className="mt-1" data-testid="filter-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "cash", "transfer", "qris", "paylater", "edc", "hutang"].map((m) => <SelectItem key={m} value={m}>{m === "all" ? "Semua" : m.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs text-slate-500">Dari Tanggal</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="mt-1" data-testid="filter-start" /></div>
        <div><Label className="text-xs text-slate-500">Sampai Tanggal</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1" data-testid="filter-end" /></div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Invoice", "Tanggal", "Pelanggan", "Barang", "IMEI", "Total", ...(isOwner ? ["Laba"] : []), "Bayar", "Aksi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={9}><Loading /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={9}><Empty /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="hover:bg-sky-50 cursor-pointer" data-testid={`sale-row-${r.id}`} onClick={() => setDetail(r)}>
                  <td className="px-4 py-3 font-mono-num text-xs">{r.invoice}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3">{r.customer_name}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.items || []).map((i, x) => <div key={x}>{i.product_name} x{i.qty}</div>)}</td>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-500">{(r.items || []).map((i) => i.imei).filter(Boolean).join(", ") || "-"}</td>
                  <td className="px-4 py-3 font-mono-num">{fmtIDR(r.total)}</td>
                  {isOwner && <td className="px-4 py-3 font-mono-num text-green-600">{fmtIDR(r.profit)}</td>}
                  <td className="px-4 py-3"><span className={`text-xs capitalize ${r.status === "paid" ? "text-green-600" : "text-amber-600"}`}>{r.payment_method} · {r.status}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="text-xs mr-1" onClick={() => setDetail(r)} data-testid={`detail-${r.id}`}>Detail</Button>
                    <Button size="sm" variant="outline" className="text-xs text-green-700 border-green-200 mr-1" onClick={() => openNota(r)} data-testid={`nota-${r.id}`}>
                      <MessageCircle className="w-3.5 h-3.5 mr-1" /> Nota
                    </Button>
                    {r.status !== "dibatalkan" && <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={() => cancelSale(r)} data-testid={`cancel-${r.id}`}>Batalkan</Button>}
                    {r.status === "dibatalkan" && <span className="text-xs text-red-500">Dibatalkan</span>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Detail Transaksi {detail?.invoice}</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-3">
              <div className="flex justify-between text-slate-500"><span>{fmtDate(detail.date)}</span><span className="capitalize">{detail.payment_method} · {detail.status}</span></div>
              {detail.bank_target && <div className="text-slate-600">Bank / Tujuan: <span className="font-medium text-slate-800">{detail.bank_target}</span></div>}
              {(detail.payments || []).some((p) => p.bank) && <div className="text-slate-600 text-xs">{detail.payments.filter((p) => p.bank).map((p, i) => <div key={i}>{p.method?.toUpperCase()}: {p.bank} ({fmtIDR(p.amount)})</div>)}</div>}
              <div className="text-slate-600">Pelanggan: <span className="font-medium text-slate-800">{detail.customer_name}</span></div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>{["Barang", "IMEI", "Qty", "Harga", "Subtotal"].map((h) => <th key={h} className="px-3 py-2 text-left font-medium text-slate-500">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {(detail.items || []).map((i, x) => (
                      <tr key={x}>
                        <td className="px-3 py-2">{i.product_name}</td>
                        <td className="px-3 py-2 font-mono-num text-xs text-slate-500">{i.imei || "-"}</td>
                        <td className="px-3 py-2 font-mono-num">{i.qty}</td>
                        <td className="px-3 py-2 font-mono-num">{fmtIDR(i.price)}</td>
                        <td className="px-3 py-2 font-mono-num">{fmtIDR(i.qty * i.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-1 font-mono-num pt-2 border-t border-slate-200">
                {detail.trade_value > 0 && <div className="flex justify-between text-amber-600"><span>Tukar Tambah</span><span>-{fmtIDR(detail.trade_value)}</span></div>}
                {detail.discount > 0 && <div className="flex justify-between text-amber-600"><span>Diskon</span><span>-{fmtIDR(detail.discount)}</span></div>}
                <div className="flex justify-between text-lg font-semibold text-slate-900"><span>Total</span><span>{fmtIDR(detail.total)}</span></div>
                <div className="flex justify-between text-slate-500"><span>Dibayar</span><span>{fmtIDR(detail.paid)}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!nota} onOpenChange={(o) => !o && setNota(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kirim Nota WhatsApp</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label className="text-xs text-slate-500">Nama Pelanggan</Label><Input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} className="mt-1" data-testid="nota-name" /></div>
            <div><Label className="text-xs text-slate-500">No. WhatsApp</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="08xxx" data-testid="nota-phone" /></div>
            <div><Label className="text-xs text-slate-500">Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs text-slate-500">IMEI</Label><Input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2 bg-slate-50 rounded-lg p-3 text-xs text-slate-500 max-h-40 overflow-y-auto whitespace-pre-wrap">{buildNota(form)}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNota(null)}>Batal</Button>
            <Button onClick={sendNota} className="bg-green-600 hover:bg-green-700" data-testid="nota-send"><MessageCircle className="w-4 h-4 mr-1.5" /> Buka WhatsApp</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
